import {
  getOffSegmentation,
  getSegmentationAnalysisType,
} from 'config/analysisConfig';
import type {
  AnalysisType,
  Segmentation,
  SegmentationControllerType,
  VOI,
} from 'config/analysisConfig';
import type { AppConfig } from 'config/config';
import {
  normalizeLesionStackSpacingMm,
  normalizeLesionStackThicknessMm,
} from 'config/config';
import { hexToRgb } from 'config/colorUtils';
import type {
  CPRContext,
  SurfaceContext,
  VRContext,
} from '@features/analysis/rendering/Panels/Renderers/vtkManagers';
import type {
  ServerCaptureBlendMode,
  ServerCaptureCPRColorItem,
  ServerCaptureCPRKind,
  ServerCaptureCPRSendInfoItem,
  ServerCaptureLightingOption,
  ServerCaptureRangeCSSendInfoItem,
  ServerCaptureSegmentationSendInfoItem,
  ServerCaptureTargetIndexItem,
} from 'utils/PacsUtilities';
import type { AutoCaptureMode } from '@features/analysis/analysisModal/AutoCaptureModal/AutoCaptureModal';
import { numbersByOrder } from 'utils/FunctionUtilites';

type ServerSegmentationKey = SegmentationControllerType | 'coronaryWall';
type CprLabelKind = 'plaque' | 'lumen';
type ServerCprAngleMode = 'stretched' | 'straightened' | 'avc';

const serverCprAngleStartDegreesByKey = new Map<string, number>();

function makeServerCprAngleKey(branchId: number, mode: ServerCprAngleMode) {
  return `${Math.round(branchId)}:${mode}`;
}

function normalizeAngleStartDegrees(angle: number | null | undefined) {
  const numericAngle = Number(angle);
  if (!Number.isFinite(numericAngle)) return undefined;
  const normalized = ((numericAngle % 360) + 360) % 360;
  return Object.is(normalized, -0) ? 0 : normalized;
}

export function setServerCprImageAngleStartDegrees({
  branchId,
  mode,
  angleStartDegrees,
}: {
  branchId: number;
  mode: ServerCprAngleMode;
  angleStartDegrees: number;
}) {
  const normalizedBranchId = Number(branchId);
  const normalizedAngle = normalizeAngleStartDegrees(angleStartDegrees);
  if (!Number.isFinite(normalizedBranchId) || normalizedAngle === undefined) {
    return;
  }

  serverCprAngleStartDegreesByKey.set(
    makeServerCprAngleKey(normalizedBranchId, mode),
    normalizedAngle,
  );
}

function getServerCprImageAngleStartDegrees(
  branchId: number,
  mode: ServerCprAngleMode,
) {
  const normalizedBranchId = Number(branchId);
  if (!Number.isFinite(normalizedBranchId)) return undefined;

  return serverCprAngleStartDegreesByKey.get(
    makeServerCprAngleKey(normalizedBranchId, mode),
  );
}

function getServerCprImageAngleStartDegreesWithModeFallback(
  branchId: number,
  mode: 'stretched' | 'straightened',
) {
  const currentModeAngle = getServerCprImageAngleStartDegrees(branchId, mode);
  if (currentModeAngle !== undefined) return currentModeAngle;

  return getServerCprImageAngleStartDegrees(
    branchId,
    mode === 'stretched' ? 'straightened' : 'stretched',
  );
}

type BuildSegmentationServerCaptureParams = {
  type: AutoCaptureMode;
  single: boolean;
  context?: SurfaceContext | VRContext | null;
  analysisType: AnalysisType;
  currentVOI: VOI;
  config: AppConfig;
  visibleKeys: string[];
  segmentation?: Segmentation;
};

type BuildCprServerCaptureParams = {
  type: Extract<
    AutoCaptureMode,
    'CPRSingleCapture' | 'CPRMainCapture' | 'CPRFullCapture'
  >;
  capture3DType: AutoCaptureMode;
  context?: SurfaceContext | VRContext | null;
  cprContexts?: {
    stretched?: CPRContext | null;
    straightened?: CPRContext | null;
  };
  analysisType: AnalysisType;
  currentVOI: VOI;
  config: AppConfig;
  visibleKeys: string[];
  segmentation?: Segmentation;
  newtype: [string, number][];
  currentBranchId?: number;
};

type BuildLesionStackServerCaptureParams = {
  currentVOI: VOI;
  config: AppConfig;
  cprContext?: CPRContext | null;
  currentBranchId?: number;
  proximalIndex?: number;
  distalIndex?: number;
  newtype: [string, number][];
};

const SERVER_TARGET_INDEX: Partial<Record<ServerSegmentationKey, number>> = {
  coronaryWall: 0,
  calcium: 1,
  la: 2,
  lv: 3,
  ra: 4,
  rv: 5,
  aorta: 6,
  coronary: 7,
  laWall: 8,
  lvWall: 9,
  raWall: 10,
  rvWall: 11,
  epicardial: 12,
  avc: 13,
};

const DEFAULT_COLOR: [number, number, number] = [255, 255, 255];

const MIP_BRIGHT_TARGET_KEYS: ServerSegmentationKey[] = [
  'aorta',
  'coronary',
  'calcium',
];

const MIP_DARK_TARGET_KEYS_BY_MODE: Record<
  AppConfig['renderingModeMIP'],
  ServerSegmentationKey[]
> = {
  AortaCoronary: [],
  AortaCoronaryWall: ['laWall', 'lvWall', 'raWall', 'rvWall'],
  All: ['la', 'lv', 'ra', 'rv', 'laWall', 'lvWall', 'raWall', 'rvWall'],
  Ventricle: ['lvWall', 'rvWall'],
};

function isSegmentationKey(key: string): key is SegmentationControllerType {
  return [
    'la',
    'laWall',
    'lv',
    'lvWall',
    'ra',
    'raWall',
    'rv',
    'rvWall',
    'aorta',
    'coronary',
    'calcium',
    'epicardial',
    'avc',
    'lowAttenuation',
    'calcified',
    'nonCalcified',
  ].includes(key);
}

function uniqueServerKeys(keys: string[]): ServerSegmentationKey[] {
  const result: ServerSegmentationKey[] = [];
  const seen = new Set<string>();

  for (const key of keys) {
    if (!(key in SERVER_TARGET_INDEX)) continue;
    if (seen.has(key)) continue;
    seen.add(key);
    result.push(key as ServerSegmentationKey);
  }

  return result.sort((a, b) => {
    const ia = SERVER_TARGET_INDEX[a] ?? Number.MAX_SAFE_INTEGER;
    const ib = SERVER_TARGET_INDEX[b] ?? Number.MAX_SAFE_INTEGER;
    return ia - ib;
  });
}

function getMipTargetKeys(
  mode: AppConfig['renderingModeMIP'] = 'All',
): ServerSegmentationKey[] {
  return uniqueServerKeys([
    ...MIP_BRIGHT_TARGET_KEYS,
    ...(MIP_DARK_TARGET_KEYS_BY_MODE[mode] ?? MIP_DARK_TARGET_KEYS_BY_MODE.All),
  ]);
}

function getVisibleKeysFromSegmentation(
  segmentation?: Segmentation,
): ServerSegmentationKey[] {
  if (!segmentation) return [];

  return uniqueServerKeys(
    Object.entries(segmentation)
      .filter(([, value]) => value?.visibility)
      .map(([key]) => key),
  );
}

function getPresetKeys(
  analysisType: AnalysisType,
  preset: 'current' | 'ventricle' | 'coronaryAorta' | 'la' | 'mip',
  visibleKeys: string[],
  segmentation?: Segmentation,
  mipMode?: AppConfig['renderingModeMIP'],
): ServerSegmentationKey[] {
  if (preset === 'mip') {
    return getMipTargetKeys(mipMode);
  }

  if (preset === 'current') {
    const visible = uniqueServerKeys(visibleKeys);
    if (visible.length > 0) return visible;

    const fromSegmentation = getVisibleKeysFromSegmentation(segmentation);
    if (fromSegmentation.length > 0) return fromSegmentation;
  }

  const analysisKeys = getSegmentationAnalysisType(analysisType) ?? [];
  const offKeys = getOffSegmentation(preset);

  return uniqueServerKeys(
    analysisKeys.filter((key) => !offKeys.includes(key)).map((key) => key),
  );
}

function getColor(
  key: ServerSegmentationKey,
  segmentation?: Segmentation,
): [number, number, number] {
  if (isSegmentationKey(key)) {
    const color = segmentation?.[key]?.color;
    if (Array.isArray(color) && color.length >= 3) {
      return [color[0], color[1], color[2]];
    }
  }

  return DEFAULT_COLOR;
}

function makeTargetIndex(
  keys: ServerSegmentationKey[],
  segmentation?: Segmentation,
): ServerCaptureTargetIndexItem[] {
  return keys.flatMap((key) => {
    const index = SERVER_TARGET_INDEX[key];
    if (index === undefined) return [];

    return [
      {
        index,
        color: getColor(key, segmentation),
      },
    ];
  });
}

function toVector3(value: unknown, fallback: [number, number, number]) {
  if (Array.isArray(value) && value.length >= 3) {
    return [Number(value[0]), Number(value[1]), Number(value[2])] as [
      number,
      number,
      number,
    ];
  }

  return fallback;
}

function toRange(value: unknown, fallback: [number, number]) {
  if (Array.isArray(value) && value.length >= 2) {
    return [Number(value[0]), Number(value[1])] as [number, number];
  }

  return fallback;
}

function getCamera(context?: SurfaceContext | VRContext | null) {
  const camera = context?.renderer?.getActiveCamera?.();
  if (!camera) {
    return {
      position: [0, 1, 0] as [number, number, number],
      focalPoint: [0, 0, 0] as [number, number, number],
      viewUp: [0, 0, 1] as [number, number, number],
      parallelProjection: true,
      parallelScale: 100,
      hasClippingRange: true,
      clippingRange: [0.1, 10000] as [number, number],
    };
  }

  const clippingRange = toRange(camera.getClippingRange?.(), [0.1, 10000]);

  return {
    position: toVector3(camera.getPosition?.(), [0, 1, 0]),
    focalPoint: toVector3(camera.getFocalPoint?.(), [0, 0, 0]),
    viewUp: toVector3(camera.getViewUp?.(), [0, 0, 1]),
    parallelProjection: Boolean(camera.getParallelProjection?.() ?? true),
    parallelScale: Number(camera.getParallelScale?.() ?? 100),
    hasClippingRange: true,
    clippingRange,
  };
}

function clampLightingAmbient(value: unknown) {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return 0.2;
  return Math.min(1, Math.max(0, numericValue));
}

function getSegmentationCaptureLightingOption(
  config: AppConfig,
  isSurfaceOn: boolean,
): ServerCaptureLightingOption {
  const lightingOption = isSurfaceOn
    ? config.lightingOptions?.Surface3D
    : config.lightingOptions?.VR;

  return {
    enabled: Boolean(lightingOption?.enabled ?? true),
    ambient: clampLightingAmbient(lightingOption?.ambient ?? 0.2),
  };
}

function makeSegmentationSendInfo(params: {
  description: string;
  captureType: 0 | 1;
  isSurfaceOn: boolean;
  blendMode: ServerCaptureBlendMode;
  heartCut: boolean;
  targetKeys: ServerSegmentationKey[];
  context?: SurfaceContext | VRContext | null;
  currentVOI: VOI;
  config: AppConfig;
  segmentation?: Segmentation;
}): ServerCaptureSegmentationSendInfoItem {
  const { currentVOI, config } = params;

  return {
    windowWidth: String(currentVOI.windowWidth ?? ''),
    windowCenter: String(currentVOI.windowLevel ?? ''),
    SliceCount: 0,
    thickness: 1,
    planar_spacing: 1,
    vertical_spacing: 1,
    screen_capture: false,
    capture_kind: 'segmentation',
    series_description: params.description,
    newWidth: config.captureWidth,
    newHeight: config.captureHeight,
    labels: null,
    draw_description: false,
    ScoutImageInfo: {
      targetindex: makeTargetIndex(params.targetKeys, params.segmentation),
      IsSurfaceOn: params.isSurfaceOn,
      IsCenterlineOn: false,
      BlendMode: params.blendMode,
      HeartCut: params.heartCut,
      CaptureType: params.captureType,
      Camera: getCamera(params.context),
      LightingOption: getSegmentationCaptureLightingOption(
        config,
        params.isSurfaceOn,
      ),
    },
  };
}

function rgbTo565([r, g, b]: [number, number, number]) {
  return ((r >> 3) << 11) | ((g >> 2) << 5) | (b >> 3);
}

function makeCprColorEntries(config: AppConfig): {
  selectedLabels: CprLabelKind[];
  colorEntries: ServerCaptureCPRColorItem[];
} {
  const labelConfig = config.label;
  const selectedLabels: CprLabelKind[] = [];
  const isPlaqueSelected = Boolean(labelConfig.plaque);
  const isLumenSelected = Boolean(labelConfig.lumen);

  if (isPlaqueSelected) selectedLabels.push('plaque');
  if (isLumenSelected) selectedLabels.push('lumen');

  const plaqueColor = (hexKey: keyof AppConfig['colors'], value: number) => ({
    selected: isPlaqueSelected,
    color: rgbTo565(hexToRgb(config.colors[hexKey] as string)),
    value,
  });


  const lumenColor: ServerCaptureCPRColorItem = {
    selected: isLumenSelected,
    color: rgbTo565(hexToRgb(config.colors.LUMEN)),
    value: 255,
  };

  return {
    selectedLabels,
    colorEntries: [
      plaqueColor('lowAttenuation', 1),
      plaqueColor('nonCalcified', 2),
      plaqueColor('CALCIUM', 3),
      lumenColor,
    ],
  };
}

function indicesOf(arr: number[], v: number): number[] {
  const idxs: number[] = [];
  for (let i = 0; i < arr.length; i++) {
    if (Object.is(arr[i], v)) idxs.push(i);
  }
  return idxs;
}

function uniqueNumberArray(values: number[]) {
  const seen = new Set<number>();
  return values.filter((value) => {
    if (seen.has(value)) return false;
    seen.add(value);
    return true;
  });
}

function getCprBranchIds(
  type: BuildCprServerCaptureParams['type'],
  config: AppConfig,
  newtype: [string, number][],
  currentBranchId?: number,
) {
  if (type === 'CPRSingleCapture') {
    return currentBranchId === undefined ? [] : [currentBranchId];
  }

  if (type === 'CPRMainCapture') {
    const branchIds = uniqueNumberArray(
      numbersByOrder(newtype, config.mainBranchOrder ?? []),
    );
    return branchIds.length > 0 && currentBranchId !== undefined
      ? uniqueNumberArray([...branchIds, currentBranchId])
      : branchIds.length > 0
        ? branchIds
        : currentBranchId === undefined
          ? []
          : [currentBranchId];
  }

  const rca = indicesOf(config.motherBranch ?? [], 0);
  const lad = indicesOf(config.motherBranch ?? [], 1);
  const lcx = indicesOf(config.motherBranch ?? [], 2);
  const orderMap: Record<string, number[]> = {
    RCA: rca,
    LAD: lad,
    LCX: lcx,
  };

  const ordered = (config.mainBranchOrder ?? []).flatMap(
    (name) => orderMap[name] ?? [],
  );

  const branchIds = uniqueNumberArray(ordered);
  return branchIds.length > 0 && currentBranchId !== undefined
    ? uniqueNumberArray([...branchIds, currentBranchId])
    : branchIds.length > 0
      ? branchIds
      : currentBranchId === undefined
        ? []
        : [currentBranchId];
}

function getBranchName(newtype: [string, number][], branchId: number) {
  return newtype?.[branchId]?.[0] ?? `Branch ${branchId}`;
}

function getCprAngleStart(
  cprContext?: CPRContext | null,
  fallbackAngleStartDegrees?: number,
) {
  const contextAngle = normalizeAngleStartDegrees(cprContext?.angle);
  if (contextAngle !== undefined) return contextAngle;

  const fallbackAngle = normalizeAngleStartDegrees(fallbackAngleStartDegrees);
  return fallbackAngle ?? 0;
}

function getCprModeContext(
  cprContexts: BuildCprServerCaptureParams['cprContexts'] | undefined,
  mode: ServerCaptureCPRKind,
) {
  return mode === 'stretched'
    ? (cprContexts?.stretched ?? cprContexts?.straightened)
    : (cprContexts?.straightened ?? cprContexts?.stretched);
}

function getCprModeOption(params: {
  branchId: number;
  mode: ServerCaptureCPRKind;
  cprContexts?: BuildCprServerCaptureParams['cprContexts'];
}) {
  return {
    angleStartDegrees: getCprAngleStart(
      getCprModeContext(params.cprContexts, params.mode),
      getServerCprImageAngleStartDegreesWithModeFallback(
        params.branchId,
        params.mode,
      ),
    ),
  };
}

function getCprCaptureModeOrder(config: AppConfig): ServerCaptureCPRKind[] {
  const allowed: ServerCaptureCPRKind[] = ['stretched', 'straightened'];
  const rawOrder = Array.isArray(config.cprSeriesOrder)
    ? config.cprSeriesOrder
    : allowed;
  const order = rawOrder
    .filter((mode): mode is ServerCaptureCPRKind =>
      allowed.includes(mode as ServerCaptureCPRKind),
    )
    .filter((mode, index, array) => array.indexOf(mode) === index);

  return order.length > 0
    ? [...order, ...allowed.filter((mode) => !order.includes(mode))]
    : allowed;
}

function isCprCombinedSeriesMode(config: AppConfig) {
  return config.cprSeriesMode === 'combined';
}

function makeCprSendInfo(params: {
  branchId: number;
  branchName: string;
  modes: ServerCaptureCPRKind[];
  context?: SurfaceContext | VRContext | null;
  cprContexts?: BuildCprServerCaptureParams['cprContexts'];
  currentVOI: VOI;
  config: AppConfig;
  visibleKeys: string[];
  segmentation?: Segmentation;
  captureType: 0 | 1;
  analysisType: AnalysisType;
  isSurfaceOn: boolean;
  blendMode: ServerCaptureBlendMode;
  heartCut: boolean;
}): ServerCaptureCPRSendInfoItem {
  const { selectedLabels, colorEntries } = makeCprColorEntries(params.config);
  const scoutKeys = getPresetKeys(
    params.analysisType,
    params.blendMode === 'MIP' ? 'mip' : 'current',
    params.visibleKeys,
    params.segmentation,
    params.config.renderingModeMIP,
  );
  const modes = params.modes.length > 0 ? params.modes : getCprCaptureModeOrder(params.config);
  const modeOptions = modes.reduce<
    Partial<Record<ServerCaptureCPRKind, { angleStartDegrees: number }>>
  >((acc, mode) => {
    acc[mode] = getCprModeOption({
      branchId: params.branchId,
      mode,
      cprContexts: params.cprContexts,
    });
    return acc;
  }, {});

  return {
    windowWidth: String(params.currentVOI.windowWidth ?? ''),
    windowCenter: String(params.currentVOI.windowLevel ?? ''),
    SliceCount: 0,
    thickness: 1,
    planar_spacing: 1,
    vertical_spacing: 1,
    screen_capture: false,
    capture_kind: modes.length === 1 ? modes[0] : modes,
    series_description: params.branchName,
    newWidth: params.config.captureWidth,
    newHeight: params.config.captureHeight,
    labels: null,
    draw_description: true,
    branchid: params.branchId,
    centerlineExpand: {
      cprFrontExpand: Number(params.config.coronary_front_expand ?? 0),
      cprBackExpand: Number(params.config.coronary_back_expand ?? 0),
    },
    ...(modeOptions.stretched ? { stretched: modeOptions.stretched } : {}),
    ...(modeOptions.straightened
      ? { straightened: modeOptions.straightened }
      : {}),
    Is_cpr_label_on: selectedLabels.length > 0,
    selected_color_labels: selectedLabels,
    color: colorEntries,
    ScoutImageInfo: {
      targetindex: makeTargetIndex(scoutKeys, params.segmentation),
      IsSurfaceOn: params.isSurfaceOn,
      IsCenterlineOn: true,
      BlendMode: params.blendMode,
      HeartCut: params.heartCut,
      CaptureType: params.captureType,
      Camera: getCamera(params.context),
      LightingOption: getSegmentationCaptureLightingOption(
        params.config,
        params.isSurfaceOn,
      ),
    },
  };
}

export function buildSegmentationServerCaptureSendInfo({
  type,
  single,
  context,
  analysisType,
  currentVOI,
  config,
  visibleKeys,
  segmentation,
}: BuildSegmentationServerCaptureParams): ServerCaptureSegmentationSendInfoItem[] {
  const captureType: 0 | 1 = single ? 1 : 1;
  const isSurfaceOn = type === 'Surface3DCapture';

  if (isSurfaceOn) {
    const targetKeys = getPresetKeys(
      analysisType,
      'current',
      visibleKeys,
      segmentation,
    );

    return [
      makeSegmentationSendInfo({
        description: 'Surface3D',
        captureType,
        isSurfaceOn: true,
        blendMode: 'VR',
        heartCut: Boolean(config.heartCut),
        targetKeys,
        context,
        currentVOI,
        config,
        segmentation,
      }),
    ];
  }

  if (single) {
    const blendMode: ServerCaptureBlendMode = config.visibleMIP ? 'MIP' : 'VR';
    const heartCut = blendMode === 'VR' && Boolean(config.heartCut);
    const targetKeys = getPresetKeys(
      analysisType,
      blendMode === 'MIP' ? 'mip' : 'current',
      visibleKeys,
      segmentation,
      config.renderingModeMIP,
    );

    return [
      makeSegmentationSendInfo({
        description: blendMode === 'MIP' ? 'MIP' : '3D Multi-view',
        captureType,
        isSurfaceOn: false,
        blendMode,
        heartCut,
        targetKeys,
        context,
        currentVOI,
        config,
        segmentation,
      }),
    ];
  }

  const infos: ServerCaptureSegmentationSendInfoItem[] = [];
  const { modelCaptureVR } = config;

  if (modelCaptureVR.current.capture) {
    const blendMode: ServerCaptureBlendMode = config.visibleMIP ? 'MIP' : 'VR';
    infos.push(
      makeSegmentationSendInfo({
        description: modelCaptureVR.current.description,
        captureType,
        isSurfaceOn: false,
        blendMode,
        heartCut: blendMode === 'VR' && Boolean(config.heartCut),
        targetKeys: getPresetKeys(
          analysisType,
          blendMode === 'MIP' ? 'mip' : 'current',
          visibleKeys,
          segmentation,
          config.renderingModeMIP,
        ),
        context,
        currentVOI,
        config,
        segmentation,
      }),
    );
  }

  if (modelCaptureVR.coronaryAorta.capture) {
    infos.push(
      makeSegmentationSendInfo({
        description: modelCaptureVR.coronaryAorta.description,
        captureType,
        isSurfaceOn: false,
        blendMode: 'VR',
        heartCut: false,
        targetKeys: getPresetKeys(
          analysisType,
          'coronaryAorta',
          visibleKeys,
          segmentation,
        ),
        context,
        currentVOI,
        config,
        segmentation,
      }),
    );
  }

  if (modelCaptureVR.ventricle.capture) {
    infos.push(
      makeSegmentationSendInfo({
        description: modelCaptureVR.ventricle.description,
        captureType,
        isSurfaceOn: false,
        blendMode: 'VR',
        heartCut: false,
        targetKeys: getPresetKeys(
          analysisType,
          'ventricle',
          visibleKeys,
          segmentation,
        ),
        context,
        currentVOI,
        config,
        segmentation,
      }),
    );
  }

  if (modelCaptureVR.ventricleHeartCut.capture) {
    infos.push(
      makeSegmentationSendInfo({
        description: modelCaptureVR.ventricleHeartCut.description,
        captureType,
        isSurfaceOn: false,
        blendMode: 'VR',
        heartCut: true,
        targetKeys: getPresetKeys(
          analysisType,
          'ventricle',
          visibleKeys,
          segmentation,
        ),
        context,
        currentVOI,
        config,
        segmentation,
      }),
    );
  }

  if (modelCaptureVR.MIP.capture) {
    infos.push(
      makeSegmentationSendInfo({
        description: modelCaptureVR.MIP.description,
        captureType,
        isSurfaceOn: false,
        blendMode: 'MIP',
        heartCut: false,
        targetKeys: getPresetKeys(
          analysisType,
          'mip',
          visibleKeys,
          segmentation,
          config.renderingModeMIP,
        ),
        context,
        currentVOI,
        config,
        segmentation,
      }),
    );
  }

  if (modelCaptureVR.la.capture) {
    infos.push(
      makeSegmentationSendInfo({
        description: modelCaptureVR.la.description,
        captureType,
        isSurfaceOn: false,
        blendMode: 'VR',
        heartCut: false,
        targetKeys: getPresetKeys(
          analysisType,
          'la',
          visibleKeys,
          segmentation,
        ),
        context,
        currentVOI,
        config,
        segmentation,
      }),
    );
  }

  if (infos.length > 0) return infos;

  return [
    makeSegmentationSendInfo({
      description: '3D Multi-view',
      captureType,
      isSurfaceOn: false,
      blendMode: config.visibleMIP ? 'MIP' : 'VR',
      heartCut: !config.visibleMIP && Boolean(config.heartCut),
      targetKeys: getPresetKeys(
        analysisType,
        config.visibleMIP ? 'mip' : 'current',
        visibleKeys,
        segmentation,
        config.renderingModeMIP,
      ),
      context,
      currentVOI,
      config,
      segmentation,
    }),
  ];
}

export function buildLesionStackServerCaptureSendInfo({
  currentVOI,
  config,
  cprContext,
  currentBranchId,
  proximalIndex,
  distalIndex,
  newtype,
}: BuildLesionStackServerCaptureParams): ServerCaptureRangeCSSendInfoItem[] {
  const branchId = Number(currentBranchId);
  const proximal = Number(proximalIndex);
  const distal = Number(distalIndex);
  const angleStartDegrees = getCprAngleStart(
    cprContext,
    getServerCprImageAngleStartDegreesWithModeFallback(branchId, 'stretched'),
  );
  const branchName = Number.isFinite(branchId)
    ? getBranchName(newtype, Math.round(branchId))
    : '';

  if (
    !Number.isFinite(branchId) ||
    !Number.isFinite(proximal) ||
    !Number.isFinite(distal)
  ) {
    return [];
  }

  return [
    {
      windowWidth: String(currentVOI.windowWidth ?? ''),
      windowCenter: String(currentVOI.windowLevel ?? ''),
      SliceCount: 0,
      thickness: 1,
      planar_spacing: 1,
      vertical_spacing: 1,
      screen_capture: false,
      capture_kind: 'range_cs',
      series_description: 'Lesion Stack',
      newWidth: config.captureWidth,
      newHeight: config.captureHeight,
      labels: null,
      draw_description: false,
      branchid: Math.round(branchId),
      centerlineExpand: {
        cprFrontExpand: Number(config.coronary_front_expand ?? 0),
        cprBackExpand: Number(config.coronary_back_expand ?? 0),
      },
      angleStartDegrees,
      range_cs_info: {
        thickness: normalizeLesionStackThicknessMm(
          config.lesionStackThicknessMm,
        ),
        spacing: normalizeLesionStackSpacingMm(config.lesionStackSpacingMm),
        proximal: Math.round(proximal),
        distal: Math.round(distal),
        branch_name: branchName,
      },
    },
  ];
}

export function buildCprServerCaptureSendInfo({
  type,
  capture3DType,
  context,
  cprContexts,
  analysisType,
  currentVOI,
  config,
  visibleKeys,
  segmentation,
  newtype,
  currentBranchId,
}: BuildCprServerCaptureParams): ServerCaptureCPRSendInfoItem[] {
  const branchIds = getCprBranchIds(type, config, newtype, currentBranchId);
  const isSurfaceOn = capture3DType === 'Surface3DCapture';
  const blendMode: ServerCaptureBlendMode =
    !isSurfaceOn && config.visibleMIP ? 'MIP' : 'VR';

  const modeOrder = getCprCaptureModeOrder(config);
  const combined = isCprCombinedSeriesMode(config);

  return branchIds.flatMap((branchId) => {
    const branchName = getBranchName(newtype, branchId);
    const groupedModes = combined
      ? [modeOrder]
      : modeOrder.map((mode) => [mode]);

    return groupedModes.map((modes) =>
      makeCprSendInfo({
        branchId,
        branchName,
        modes,
        context,
        cprContexts,
        currentVOI,
        config,
        visibleKeys,
        segmentation,
        captureType: 0,
        analysisType,
        isSurfaceOn,
        blendMode,
        heartCut: blendMode === 'VR' && Boolean(config.heartCut),
      }),
    );
  });
}
